import { useState, useEffect } from "react";
import { Bell, AlertTriangle, CheckCheck } from "lucide-react";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { fetchAlerts, markAlertsRead } from "@/lib/api";

export default function AlertsBell({ onOpenSite }) {
  const [alerts, setAlerts] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);

  const load = async () => {
    try {
      const d = await fetchAlerts();
      setAlerts(d.alerts || []);
      setUnread(d.unread || 0);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { load(); }, []);

  const handleOpen = async (o) => {
    setOpen(o);
    if (o && unread > 0) {
      await markAlertsRead();
      setUnread(0);
      setAlerts((a) => a.map((x) => ({ ...x, read: true })));
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="alerts-bell"
          className="tap-safe relative inline-flex w-11 items-center justify-center rounded-btn border border-line lg:w-control text-navy-800 transition-colors duration-control ease-editorial hover:bg-offwhite focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 focus-visible:ring-offset-2 focus-visible:ring-offset-offwhite"
          aria-label={unread > 0 ? `Alerts, ${unread} unread` : "Alerts"}
        >
          <Bell size={17} aria-hidden="true" />
          {unread > 0 && (
            <span data-testid="alerts-badge" className="tabular absolute -right-1.5 -top-1.5 grid h-[18px] min-w-[18px] place-items-center rounded-full bg-amber-700 px-1 text-[10px] font-bold text-white">
              {unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" data-testid="alerts-panel" className="w-[340px] border-line bg-white p-0">
        <div className="flex items-center justify-between border-b border-line px-4 py-3">
          <h4 className="font-heading text-h3 text-navy-950">Attention alerts</h4>
          {alerts.length > 0 && <CheckCheck size={16} className="text-slate-500" aria-hidden="true" />}
        </div>
        <div className="max-h-[360px] overflow-y-auto">
          {alerts.length === 0 ? (
            <div data-testid="alerts-empty" className="px-4 py-8 text-center text-small text-slate-600">
              No alerts. We'll ping you the moment a site flips to "Needs attention".
            </div>
          ) : (
            alerts.map((a, i) => (
              <button
                key={a.id}
                onClick={() => { onOpenSite?.(a.property_id); setOpen(false); }}
                data-testid={`alert-item-${i}`}
                className={`flex w-full items-start gap-3 border-b border-line px-4 py-3 text-left transition-colors duration-control last:border-0 hover:bg-offwhite focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-emerald-600 ${!a.read ? "bg-amber-50" : ""}`}
              >
                <div className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-compact bg-amber-50 text-amber-700">
                  <AlertTriangle size={16} aria-hidden="true" />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-small font-semibold text-navy-950">{a.property_name}</span>
                    {!a.read && (
                      <>
                        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" aria-hidden="true" />
                        <span className="sr-only">Unread</span>
                      </>
                    )}
                  </div>
                  <p className="mt-0.5 text-xs leading-snug text-slate-600">Flipped to <span className="font-semibold text-amber-700">Needs attention</span> — {a.headline}</p>
                  <span className="tabular text-[12px] text-slate-600">{new Date(a.created_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                </div>
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
